import { IconJobs, useStyletron } from '@olxeu-jobs/ui';
import { FormattedMessage, useFormatIndustry, useFormatMessage } from '~i18n';
import type { CompanyProfileCarouselProfileFragment } from '~api';
import { Logo } from '~features/logo';
import { Link } from '~features/router';
import { EmployerCardLayout } from './employer-card-layout';
import { Title, Industry, IndustryTitle, LogoWrapper } from './styled-components';
import { Banner } from './banner';

export type Props = Omit<CompanyProfileCarouselProfileFragment, '__typename' | 'id' | 'slug'> & {
  headingLevel: 2 | 3 | 4 | 5 | 6;
  onLinkClick: () => void;
  publicPath: string;
  target?: '_blank' | '_self';
};

export const EmployerCard = ({
  bannerUrl,
  companyName,
  logoUrl,
  industry,
  headingLevel,
  onLinkClick,
  publicPath,
  target = '_self',
}: Props) => {
  const formatMessage = useFormatMessage();
  const formatIndustry = useFormatIndustry();
  const [css, theme] = useStyletron();

  return (
    <Link
      to={publicPath}
      target={target}
      // eslint-disable-next-line formatjs/no-literal-string-in-jsx
      aria-label={`${companyName} - ${formatMessage({
        id: 'comp.company_profile',
        defaultMessage: 'Company Profile',
      })}`}
      onClick={onLinkClick}
      className={css({
        textDecoration: 'none',
      })}
    >
      <EmployerCardLayout
        header={
          <>
            <Banner url={bannerUrl} />
            {!!companyName && !!logoUrl && (
              <LogoWrapper>
                <Logo companyName={companyName} url={logoUrl} />
              </LogoWrapper>
            )}
          </>
        }
        content={
          <>
            <Title $as={`h${headingLevel}` as const} $variant="regular">
              {companyName}
            </Title>
            <Industry>
              <IconJobs
                size={12}
                $style={{ flex: '0 0 1', marginRight: theme.space[4], minWidth: theme.space[12] }}
              />
              <FormattedMessage
                id="comp.employers.label.industry"
                defaultMessage="Hiring in: <strong>{industry}</strong>"
                values={{
                  industry: formatIndustry(industry) as string,
                  strong: (chunks: string) => <IndustryTitle>{chunks}</IndustryTitle>,
                }}
              />
            </Industry>
          </>
        }
      />
    </Link>
  );
};
